import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useTheme } from '../context/ThemeContext';
import './Navbar.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const { subscription } = useSubscription();
  const { workspaces, currentWorkspace, switchWorkspace, canCreateWorkspace } = useWorkspace();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    setWsMenuOpen(false);
  }, [location]);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  function handleHashLink(e, to) {
    if (!to.includes('#')) return;
    e.preventDefault();
    setMobileOpen(false);
    const [path, hash] = to.split('#');
    const target = path === '' || path === '/' ? '/' : path;
    const scrollToHash = () => {
      const el = document.getElementById(hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };
    if (location.pathname === target || (target === '/' && location.pathname === '/')) {
      scrollToHash();
    } else {
      navigate(target);
      setTimeout(scrollToHash, 400);
    }
  }

  const ADMIN_EMAILS = ['gabriela.ormazabal@gormaran-marketing.com', 'gabriela.ormazabal@gmail.com'];
  const isAdmin = ADMIN_EMAILS.includes(currentUser?.email?.toLowerCase().trim());

  const navLinks = currentUser
    ? [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/pricing', label: 'Precios' },
        { to: '/blog', label: 'Blog' },
        { to: '/academia', label: 'Academia' },
        ...(isAdmin ? [{ to: '/admin/blog', label: '✏️ Blog Admin' }] : []),
      ]
    : [
        { to: '/#how-it-works', label: 'Cómo funciona' },
        { to: '/pricing', label: 'Precios' },
        { to: '/blog', label: 'Blog' },
        { to: '/academia', label: 'Academia' },
      ];

  const isActive = (to) => location.pathname === to;

  return (
    <motion.nav
      className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="navbar__inner">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <img src="/TP-favicon-gormaran-SaaS.png" alt="Gormaran" className="navbar__logo-img" />
          <span className="navbar__logo-text">
            <span className="gradient-text">Gormaran</span>
            <span className="navbar__logo-sub">AI Growth Hub</span>
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="navbar__links">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`navbar__link ${isActive(link.to) ? 'navbar__link--active' : ''}`}
              onClick={link.to.includes('#') ? (e) => handleHashLink(e, link.to) : undefined}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right section */}
        <div className="navbar__right">
          {/* Theme toggle */}
          <button
            className="navbar__theme-btn"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          {currentUser ? (
            <div className="navbar__user">
              {/* Plan badge */}
              <span className={`badge ${subscription === 'free' ? 'badge-free' : 'badge-pro'}`}>
                {
                  subscription === 'free' ? 'Free' :
                  subscription === 'grow' ? '⭐ Grow' :
                  subscription === 'scale' ? '💎 Scale' :
                  subscription === 'evolution' ? '🚀 Evolution' :
                  subscription === 'admin' ? '🔑 Admin' :
                  subscription
                }
              </span>

              {/* Workspace switcher */}
              <div className="navbar__ws">
                <button
                  className="navbar__ws-btn"
                  onClick={() => { setWsMenuOpen(!wsMenuOpen); setUserMenuOpen(false); }}
                  title="Switch workspace"
                >
                  <span className="navbar__ws-emoji">{currentWorkspace.emoji}</span>
                  <span className="navbar__ws-name">{currentWorkspace.name}</span>
                  <span className={`navbar__lang-arrow ${wsMenuOpen ? 'open' : ''}`}>▾</span>
                </button>

                <AnimatePresence>
                  {wsMenuOpen && (
                    <motion.div
                      className="navbar__ws-dropdown"
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className="navbar__ws-header">Espacios de trabajo</div>
                      {workspaces.map(ws => (
                        <button
                          key={ws.id}
                          className={`navbar__ws-option ${ws.id === currentWorkspace.id ? 'navbar__ws-option--active' : ''}`}
                          onClick={() => { switchWorkspace(ws.id); setWsMenuOpen(false); }}
                        >
                          <span>{ws.emoji}</span>
                          <span>{ws.name}</span>
                          {ws.id === currentWorkspace.id && <span className="navbar__ws-check">✓</span>}
                        </button>
                      ))}
                      <div className="navbar__dropdown-divider" />
                      {canCreateWorkspace ? (
                        <button
                          className="navbar__ws-option navbar__ws-option--new"
                          onClick={() => { setWsMenuOpen(false); navigate('/settings#workspaces'); }}
                        >
                          <span>＋</span>
                          <span>Nuevo espacio</span>
                        </button>
                      ) : (
                        <button
                          className="navbar__ws-option navbar__ws-option--upgrade"
                          onClick={() => { setWsMenuOpen(false); navigate('/pricing'); }}
                        >
                          <span>🔒</span>
                          <span>Mejora tu plan</span>
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Profile icon + dropdown */}
              <button
                className="navbar__avatar"
                onClick={() => { setUserMenuOpen(!userMenuOpen); }}
                aria-label="User menu"
              >
                {currentUser.photoURL ? (
                  <img src={currentUser.photoURL} alt={currentUser.displayName} />
                ) : (
                  <span>{(currentUser.displayName || currentUser.email || 'U')[0].toUpperCase()}</span>
                )}
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    className="navbar__dropdown"
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="navbar__dropdown-header">
                      <strong>{currentUser.displayName || 'Usuario'}</strong>
                      <small>{currentUser.email}</small>
                    </div>
                    <div className="navbar__dropdown-divider" />
                    <Link to="/dashboard" className="navbar__dropdown-item">📊 Dashboard</Link>
                    <Link to="/settings" className="navbar__dropdown-item">⚙️ Ajustes</Link>
                    <Link to="/pricing" className="navbar__dropdown-item">💳 Mejorar plan</Link>
                    <div className="navbar__dropdown-divider" />
                    <button className="navbar__dropdown-item navbar__dropdown-item--danger" onClick={handleLogout}>
                      🚪 Cerrar sesión
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="navbar__auth">
              <Link to="/auth" className="btn btn-ghost btn-sm">Iniciar sesión</Link>
              <Link to="/auth?mode=register" className="btn btn-primary btn-sm">Empezar gratis</Link>
            </div>
          )}

          {/* Mobile menu button */}
          <button
            className="navbar__mobile-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span className={`navbar__hamburger ${mobileOpen ? 'open' : ''}`}>
              <span /><span /><span />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="navbar__mobile-link"
                onClick={link.to.includes('#') ? (e) => handleHashLink(e, link.to) : undefined}
              >
                {link.label}
              </Link>
            ))}
            {currentUser ? (
              <>
                <Link to="/settings" className="navbar__mobile-link">Ajustes</Link>
                <button className="navbar__mobile-link navbar__mobile-link--danger" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </>
            ) : (
              <>
                <Link to="/auth" className="navbar__mobile-link">Iniciar sesión</Link>
                <Link to="/auth?mode=register" className="navbar__mobile-link navbar__mobile-link--cta">
                  Empezar gratis
                </Link>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop for user menu, lang menu, or ws menu */}
      {(userMenuOpen || wsMenuOpen) && (
        <div className="navbar__backdrop" onClick={() => { setUserMenuOpen(false); setWsMenuOpen(false); }} />
      )}
    </motion.nav>
  );
}
